import React, { useState, useEffect, useContext } from "react";
import { Card, Spin, Carousel, Typography } from "antd";
import { PublicContext } from "../PublicContext";
import "../../styles/custom.css";
import dayjs from 'dayjs';

const { Meta } = Card;

export default function PromoCarousel() {
    const [loading, setLoading] = useState(true);
    const { uniquePromotions, products, selectedStore, storeProducts, promotionProducts } = useContext(PublicContext);
    const { Title } = Typography;

    useEffect(() => {
        if (uniquePromotions != null) {
            setLoading(false);
        }
    }, [uniquePromotions]);

    // Check stock status for all items in a promotion
    const getPromotionStockStatus = (promotionId) => {
        if (!promotionProducts || !storeProducts || !selectedStore.current?.storeCode || !products) {
            return { allInStock: true, allOutOfStock: false, itemStatuses: [] };
        }

        // Get all products associated with this promotion (both Buy and Get items)
        const promoItems = promotionProducts.filter(pp => pp.promotionId === promotionId);
        
        if (promoItems.length === 0) {
            return { allInStock: true, allOutOfStock: false, itemStatuses: [] };
        }

        // Check stock status for each item
        const itemStatuses = promoItems.map(promoItem => {
            const productCode = promoItem.buyProductCode || promoItem.getProductCode;
            if (!productCode) return null;

            // Get product name
            const product = products.find(p => p.productCode === productCode);
            const productName = product?.productName || productCode;

            // Check if this product is in stock for the selected store
            const storeProduct = storeProducts.find(
                sp => sp.productCode === productCode && 
                      sp.storeCode === selectedStore.current.storeCode
            );

            const inStock = storeProduct && storeProduct.isInStock;

            return {
                productCode,
                productName,
                inStock,
                type: promoItem.buyProductCode ? 'buy' : 'get'
            };
        }).filter(item => item !== null);

        const inStockCount = itemStatuses.filter(item => item.inStock).length;
        const allInStock = inStockCount === itemStatuses.length;
        const allOutOfStock = inStockCount === 0;

        return { allInStock, allOutOfStock, itemStatuses };
    };

    function renderProductsTable(products) {
        const currentDate = dayjs();
        let storePromotions;
        if (localStorage.getItem("StorePromotions")) {
            storePromotions = JSON.parse(localStorage.getItem("StorePromotions")).filter(sp => sp.storeCode === selectedStore.current.storeCode && sp.isActive);
        }
        else {
            window.location.href = 'https://www.mandarinrestaurant.com/order-online/';
        }        

        let sortedUniquePromotions = uniquePromotions
            .filter(
                promotion =>
                    promotion.displayOrder >= 0 &&
                    currentDate.isAfter(promotion.activationDate) &&
                    currentDate.isBefore(promotion.expiryDate) &&
                    storePromotions.some(sp => sp.promotionId === promotion.id)
            )
            .sort((a, b) => a.displayOrder - b.displayOrder);
        return (
            <Carousel id="PromoCarousel" autoplay dots={true}>
                {sortedUniquePromotions.map((promotion) => {
                    if (promotion.promotionPicture.slice(-3) === "jpg" || promotion.promotionPicture.slice(-3) === "png") {
                        const stockStatus = getPromotionStockStatus(promotion.id);
                        const { allInStock, allOutOfStock, itemStatuses } = stockStatus;
                        const hasPartialStock = !allInStock && !allOutOfStock;
                        
                        return (
                            <div
                                key={promotion.id}
                                className="carousel-item"
                                style={{ position: "relative" }}
                            >
                                {promotion.promotionPicture.substring(0, 10) === "background" ?
                                    <div style={{ position: "relative", aspectRatio: "3.3693/1", width: "100%", height: "auto", overflow: "hidden" }}>
                                        <img
                                            alt={promotion.promotionName}
                                            src={`https://takeoutpictures.blob.core.windows.net/weborderpictures/promotion/` + promotion.promotionPicture}
                                            style={{ 
                                                width: "100%", 
                                                aspectRatio: "3.3693/1", 
                                                height: "auto", 
                                                objectFit: "cover",
                                                filter: allOutOfStock ? "grayscale(100%) brightness(0.7)" : "none",
                                                opacity: allOutOfStock ? 0.6 : 1
                                            }}
                                        />
                                        <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }} >
                                            <p className="TextPromoCarouselTitle text-uppercase">{promotion.promotionName}</p>
                                            <p className="TextPromoCarouselDescription">{promotion.promotionDescription}</p>
                                        </div>
                                        {(allOutOfStock || hasPartialStock) && (
                                            <div style={{
                                                position: "absolute",
                                                bottom: "0",
                                                left: "0",
                                                right: "0",
                                                backgroundColor: "rgba(0, 0, 0, 0.75)",
                                                backdropFilter: "blur(4px)",
                                                padding: "12px 16px",
                                                zIndex: 10
                                            }}>
                                                {allOutOfStock ? (
                                                    <div style={{ 
                                                        color: "white",
                                                        fontSize: "14px",
                                                        fontWeight: "600",
                                                        textAlign: "center"
                                                    }}>
                                                        Temporarily sold out
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <div style={{ 
                                                            color: "white",
                                                            fontSize: "13px",
                                                            fontWeight: "600",
                                                            marginBottom: "6px"
                                                        }}>
                                                            Some items unavailable
                                                        </div>
                                                        <div style={{ 
                                                            display: "flex",
                                                            flexWrap: "wrap",
                                                            gap: "6px"
                                                        }}>
                                                            {itemStatuses.map((item, idx) => (
                                                                <span key={idx} style={{
                                                                    display: "inline-flex",
                                                                    alignItems: "center",
                                                                    padding: "2px 8px",
                                                                    borderRadius: "12px",
                                                                    fontSize: "11px",
                                                                    fontWeight: "500",
                                                                    backgroundColor: item.inStock ? "rgba(82, 196, 26, 0.9)" : "rgba(255, 77, 79, 0.9)",
                                                                    color: "white"
                                                                }}>
                                                                    {item.inStock ? "✓ " : "✗ "}{item.productName}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    :
                                    <div style={{ position: "relative" }}>
                                        <img
                                            alt={promotion.promotionName}
                                            src={`https://takeoutpictures.blob.core.windows.net/weborderpictures/promotion/` + promotion.promotionPicture}
                                            style={{ 
                                                aspectRatio: "3.3693/1", 
                                                width: "100%", 
                                                height: "auto", 
                                                objectFit: "cover",
                                                filter: allOutOfStock ? "grayscale(100%) brightness(0.7)" : "none",
                                                opacity: allOutOfStock ? 0.6 : 1
                                            }}
                                            onError={(e) => {
                                                e.target.src = "https://takeoutpictures.blob.core.windows.net/weborderpictures/medium/img-placeholder.png";
                                                e.target.onerror = null;
                                            }}
                                        />
                                        {(allOutOfStock || hasPartialStock) && (
                                            <div style={{
                                                position: "absolute",
                                                bottom: "0",
                                                left: "0",
                                                right: "0",
                                                backgroundColor: "rgba(0, 0, 0, 0.75)",
                                                backdropFilter: "blur(4px)",
                                                padding: "12px 16px",
                                                zIndex: 10
                                            }}>
                                                {allOutOfStock ? (
                                                    <div style={{ 
                                                        color: "white",
                                                        fontSize: "14px",
                                                        fontWeight: "600",
                                                        textAlign: "center"
                                                    }}>
                                                        Temporarily sold out
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <div style={{ 
                                                            color: "white",
                                                            fontSize: "13px",
                                                            fontWeight: "600",
                                                            marginBottom: "6px"
                                                        }}>
                                                            Some items unavailable
                                                        </div>
                                                        <div style={{ 
                                                            display: "flex",
                                                            flexWrap: "wrap",
                                                            gap: "6px"
                                                        }}>
                                                            {itemStatuses.map((item, idx) => (
                                                                <span key={idx} style={{
                                                                    display: "inline-flex",
                                                                    alignItems: "center",
                                                                    padding: "2px 8px",
                                                                    borderRadius: "12px",
                                                                    fontSize: "11px",
                                                                    fontWeight: "500",
                                                                    backgroundColor: item.inStock ? "rgba(82, 196, 26, 0.9)" : "rgba(255, 77, 79, 0.9)",
                                                                    color: "white"
                                                                }}>
                                                                    {item.inStock ? "✓ " : "✗ "}{item.productName}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                }
                            </div>
                        );
                    }
                    return null;
                })}
            </Carousel>
        );
    }

    function renderLoading() {
        return <Spin />;
    }

    return (
        <div>
            {loading ? renderLoading() : renderProductsTable(products)}
        </div>
    );
}
